/* eslint-disable max-len */
export const privacyPolicy = {
  headings: [
    'Information Handled Through Steam',
    'Information Stored On Your Device',
    'Multiplayer Connections',
    'Optional Feedback Service',
    'Website Analytics',
    'What We Do Not Do',
    "Children's Privacy",
    'Your Rights',
    'Changes To This Policy',
    'Contact Us',
  ],
  text: `Privacy Policy
Last updated: August 17, 2026

OutHereGaming LLC ("we", "us", "our") is the developer of "Warm" ("the Game"), a game distributed on Steam. This Privacy Policy explains what information the Game and its optional feedback service handle and how that information is used.

In short: the Game does not use OutHereGaming accounts, analytics, advertising, or cloud crash reporting, and gameplay information stays on your device or is handled by Valve as part of Steam. This website uses Google Analytics to measure how visitors find and move through the site, which is described under Website Analytics below. If you voluntarily use our website feedback form, we store the feedback and any screenshot you choose to submit so we can evaluate and improve the Game.

Information Handled Through Steam

The Game uses Steam for multiplayer, saved games, and achievements. When you play, the following information is handled by Steam:

Your Steam ID and Steam display name. These are used to connect you to other players, to show who is in your session, and to display your name in game.

Saved games and progress. If Steam Cloud is enabled on your account, your saved settings, journal entries, and lifetime statistics are stored in your Steam Cloud storage so they follow you between computers.

Achievements and statistics. Progress toward the Game's achievements is recorded against your Steam account.

Voice chat. If you enable voice chat, your microphone audio is transmitted to the other players in your session through Steam's networking. We do not record, store, or process this audio, and it does not pass through any system we control.

This information is governed by Valve's Privacy Policy, which is available at https://store.steampowered.com/privacy_agreement/. We do not receive a copy of it and we cannot access your Steam account.

Information Stored On Your Device

The Game stores your settings, preferences, chosen display name, journal entries, and lifetime statistics locally on your computer. The Game may also write diagnostic log files locally to help troubleshoot crashes. This information stays on your device unless Steam Cloud syncs your saved data to your Steam account. You can remove it at any time by deleting the Game's local files through Steam.

Multiplayer Connections

Multiplayer sessions connect players to each other through Steam's networking service. The Game does not display your IP address to other players. Steam may process IP addresses and other connection information and may connect players directly or through Valve's relay network. That processing is controlled by Valve and is subject to Valve's Privacy Policy.

Optional Feedback Service

The Game includes a Feedback button that opens a form at https://www.outheregaming.com/feedback in your web browser. Using this form is optional. If you submit feedback, we may collect:

The feedback category, optional rating and frequency, comments you enter, and a unique feedback reference number.

The Game version, platform, and source included in the feedback link.

Your browser's user-agent string, which generally identifies the browser, operating system, and device type.

An optional screenshot you choose to upload. Your comments or screenshot may contain personal information if you include it, so please do not submit private, confidential, or sensitive information.

We use this information only to review feedback, investigate problems, and improve the Game. Feedback records are stored in Amazon DynamoDB, and screenshots are stored privately in Amazon S3. Amazon Web Services processes IP addresses and request metadata as necessary to deliver and secure the service, but we do not add your IP address to the stored feedback record. Incomplete screenshot uploads are automatically deleted after one day. Completed feedback and screenshots are kept only as long as reasonably needed to evaluate or act on the feedback.

Website Analytics

This website uses Google Analytics 4, a service provided by Google, to understand how visitors find and use the site. The Game itself contains no analytics; this applies only to https://www.outheregaming.com.

Google Analytics sets cookies in your browser and records:

Which pages you view, in what order, and how long you spend on them.

An approximate location derived from your IP address and resolved no more precisely than city level. Google Analytics does not retain your full IP address.

Your device type, browser, operating system, screen size, and the website or search engine that referred you.

Three specific actions we have asked it to record: opening our Steam store page, playing the trailer, and successfully sending the feedback form. For feedback we record only the category you selected. We never send your comments, your screenshot, or your feedback reference number to Google.

We use this only in aggregate, to see which pages lead players to the Steam page. We do not use it for advertising, we do not enable ad personalisation or Google Signals, and we do not link it to the feedback you submit. Google acts as our data processor for this information, and its handling is subject to Google's Privacy Policy at https://policies.google.com/privacy.

You can opt out at any time by installing Google's browser add-on at https://tools.google.com/dlpage/gaoptout, by enabling tracking protection in your browser, or by blocking this site's cookies. Nothing on this website depends on analytics, so blocking it changes nothing about how the site works for you.

What We Do Not Do

We do not operate accounts, logins, or registration for the Game or website.
We do not process payments or store payment information.
We do not use advertising cookies, web beacons, or advertising identifiers. The only cookies this website sets are the Google Analytics cookies described above.
The Game does not use analytics, advertising, telemetry, or cloud crash reporting services, and our website analytics are limited to Google Analytics as described above.
We do not sell or rent personal information.
We do not ask for your name, address, email address, or phone number through the Game or feedback form.

Children's Privacy

The Game and feedback service are not directed at children under 13. We do not knowingly collect personal information from children. Because the Game relies on Steam accounts, age requirements are also governed by Valve's Steam Subscriber Agreement. Do not submit feedback containing personal information about a child.

Your Rights

To manage data held by Steam, use the privacy and data settings in your Steam account. To ask us to access, correct, or delete submitted feedback, contact us at the address below and include the feedback reference number shown after submission when available. We may need additional information to identify the record.

Changes To This Policy

We may update this Privacy Policy from time to time. When we do, we will update the date at the top of this page, include the revised policy with the Game, and publish it at https://www.outheregaming.com/privacy.

Contact Us

For questions or requests concerning this Privacy Policy, contact us at dev@outheregaming.com.`,
};

export const endUserLicense = {
  headings: [
    'License Grant',
    'Ownership',
    'License Restrictions',
    'Platform Terms',
    'Updates and Online Features',
    'User Content and Feedback',
    'Termination',
    'Disclaimer of Warranties',
    'Limitation of Liability',
    'Governing Law',
    'Contact',
  ],
  text: `End User License Agreement
Last updated: March 26, 2026

This End User License Agreement ("EULA") is a legal agreement between you and OutHereGaming LLC ("OutHereGaming", "we", "us", or "our") for the game Warm and any patches, updates, downloadable content, and related materials we provide (collectively, the "Game").

By installing, launching, or using Warm, you agree to this EULA. If you do not agree, do not install or use the Game.

License Grant

Subject to your compliance with this EULA and any applicable platform terms, OutHereGaming grants you a limited, personal, non-exclusive, non-transferable, non-sublicensable, revocable license to install and use one copy of Warm for your personal, non-commercial entertainment purposes.

Ownership

Warm is licensed, not sold. OutHereGaming and its licensors retain all rights, title, and interest in and to the Game, including all intellectual property rights.

License Restrictions

You may not, except where applicable law expressly permits:

Copy, reproduce, distribute, or publicly display the Game
Sell, rent, lease, sublicense, or otherwise transfer the Game
Reverse engineer, decompile, or disassemble the Game
Modify, adapt, translate, or create derivative works from the Game
Use cheats, automation, exploits, or unauthorized third-party software
Remove copyright, trademark, or proprietary notices

Platform Terms

If you obtained Warm through Steam or another storefront, your use of that platform is also subject to the platform's own subscriber agreement, store terms, and rules. If this EULA conflicts with mandatory platform terms, those platform terms control only to the extent of the conflict.

Updates and Online Features

We may provide patches, updates, or changes to Warm at any time. Some features may require internet access, platform services, or acceptance of additional notices. We may modify or discontinue online features with or without notice.

User Content and Feedback

If you submit feedback, suggestions, bug reports, or other materials related to Warm, you grant OutHereGaming a worldwide, perpetual, irrevocable, sublicensable, royalty-free license to use that feedback for any purpose without compensation to you.

Termination

This EULA remains effective until terminated. It automatically terminates if you fail to comply with its terms. Upon termination, you must stop using Warm and uninstall or destroy all copies in your possession or control.

Disclaimer of Warranties

To the fullest extent permitted by law, Warm is provided "as is" and "as available", without warranties of any kind, whether express, implied, or statutory, including implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.

Limitation of Liability

To the fullest extent permitted by law, OutHereGaming and its licensors will not be liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, data, goodwill, or business interruption, arising out of or related to your use of or inability to use Warm.

Governing Law

This EULA is governed by the laws of the State of Wyoming, without regard to conflict of laws principles. Any dispute arising from this EULA will be resolved in the state or federal courts located in Wyoming, and you consent to that jurisdiction and venue.

Contact

Questions about this EULA may be sent to dev@outheregaming.com.`,
};

export const termsOfService = {
  headings: [
    'Purchase and Distribution',
    'License',
    'Intellectual Property',
    'Multiplayer and Player Conduct',
    'Feedback Submissions',
    'Availability and Modifications',
    'Termination',
    'Age Requirement',
    'No Warranty',
    'Limitation Of Liability',
    'Indemnification',
    'Privacy',
    'Dispute Resolution',
    'Force Majeure',
    'Changes To These Terms',
    'Contact Us',
  ],
  text: `Terms and Conditions
Last updated: August 4, 2026

These Terms and Conditions ("Terms") govern your use of "Warm" ("the Game"), developed and published by OutHereGaming LLC ("us", "we", or "our"). Please read these Terms carefully before playing.

By installing or playing the Game, you agree to be bound by these Terms. If you disagree with any part of these Terms, do not play the Game.

Purchase and Distribution

The Game is sold and distributed through Steam. All purchases, payments, refunds, and billing are handled by Valve Corporation under the Steam Subscriber Agreement, available at https://store.steampowered.com/subscriber_agreement/. We do not process payments and we do not sell anything inside the Game. Refund requests must be made through Steam.

License

Subject to these Terms, we grant you a personal, non-exclusive, non-transferable license to install and play the Game for your own private, non-commercial use. Your license is also subject to the terms of the platform you purchased the Game through.

You may create and share videos, streams, screenshots, and similar recordings of your own gameplay, including on monetized channels.

You may not sell, redistribute, or commercially exploit the Game itself, nor reverse engineer, decompile, or disassemble it except to the extent that applicable law expressly permits.

Intellectual Property

The Game and its original content, features, and functionality are and will remain the exclusive property of OutHereGaming LLC and its licensors. The Game is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.

The Game includes third-party software used under license. Those components remain the property of their respective owners and are credited in the Game.

Multiplayer and Player Conduct

The Game offers online multiplayer, including optional voice chat, connecting players to one another through Steam. We do not host, monitor, moderate, or record multiplayer sessions or voice chat.

You are responsible for your own conduct when playing with others. You agree not to use the Game to harass, threaten, or abuse other players, or to do anything unlawful. Because sessions are hosted by players rather than by us, we cannot intervene in disputes between players. Steam provides tools to block and report other users.

You are responsible for the display name you choose. Do not use a name that is unlawful, infringing, or designed to harass others.

Feedback Submissions

The Game may open an optional feedback form on our website. You may submit comments, ratings, technical context, and an optional screenshot. Do not submit personal, confidential, unlawful, infringing, or harmful material, or content you do not have the right to share.

You retain ownership of content you submit. By submitting feedback, you grant OutHereGaming LLC a worldwide, perpetual, irrevocable, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and create derivative works from that feedback and submitted content for the purpose of developing, improving, supporting, and promoting the Game.

Feedback is voluntary and is not confidential. We are not required to use, respond to, or compensate you for a submission. Our Privacy Policy explains how feedback and optional screenshots are handled.

Availability and Modifications

We may update, modify, or discontinue the Game or any of its features at any time. Online multiplayer depends on Steam's services and may become unavailable if those services change or are discontinued.

Termination

These Terms apply for as long as you use the Game. Your license terminates automatically if you breach these Terms. Access to the Game through Steam is additionally governed by the Steam Subscriber Agreement.

Age Requirement

The Game requires a Steam account. Age eligibility for Steam accounts is governed by the Steam Subscriber Agreement. The Game and feedback service are not directed at children under 13. If you are a minor, you should review these Terms with a parent or legal guardian before playing or submitting feedback.

No Warranty

The Game and feedback service are provided on an "as is" and "as available" basis without any warranty or condition, express, implied, or statutory. OutHereGaming LLC, its subsidiaries, affiliates, and its licensors do not warrant that the Game or feedback service will be uninterrupted, error-free, or completely secure. Some jurisdictions do not allow the exclusion of certain warranties, so some of these exclusions may not apply to you.

Limitation Of Liability

In no event shall OutHereGaming LLC, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Game or feedback service; (ii) any conduct or content of any third party in the Game; (iii) any content obtained from the Game; and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its essential purpose. Some jurisdictions do not allow the limitation of certain damages, so some of these limitations may not apply to you.

Indemnification

You agree to indemnify and hold harmless OutHereGaming LLC and its officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, or expenses, including attorneys' fees and costs, arising out of or in any way connected with your use of the Game or feedback service, content you submit, or your violation of these Terms.

Privacy

Our Privacy Policy describes what information the Game and feedback service handle and how it is used. It is available in the Game and at https://www.outheregaming.com/privacy and is incorporated into these Terms by reference.

Dispute Resolution

If you have a dispute with us, you agree that before taking any formal action you will contact us at dev@outheregaming.com and provide a brief written description of the dispute and your contact information. If the dispute is not resolved within 60 days after submission, you agree that any formal proceedings must be brought in the federal or state courts located in Wyoming, United States. You and we consent to venue and personal jurisdiction there. Nothing in this section prevents either party from seeking relief in small claims court.

Force Majeure

We will not be considered in breach of these Terms if we are unable to perform, or are delayed in performing, any obligation under these Terms due to circumstances beyond our reasonable control. Such circumstances include, but are not limited to, acts of god, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, strikes, labor disputes, or shortages of transportation, facilities, fuel, energy, labor or materials.

Changes To These Terms

We reserve the right to modify or replace these Terms at any time. When we do, we will update the date at the top of this page, include the revised Terms with the Game, and publish them at https://www.outheregaming.com/terms. By continuing to play after those revisions take effect, you agree to be bound by the revised Terms.

Contact Us

If you have any questions about these Terms, contact us at dev@outheregaming.com.`,
};
